<template>
  <div>
    <!-- Login/Register Modal -->
    <transition name="fade">
      <div v-if="showLogin" class="login-modal-overlay">
        <div class="login-modal">
          <div class="x-modal-header">
            <close-icon class="close-button" @click="handleCloseClick"></close-icon>
          </div>
          <!-- The hero sells whatever the seeker just reached for. The theme is
               set by utils/request-auth at the gate; with no theme (the "Reveal
               Myself" pill, the Studio guard) it falls back to the general
               pitch. Copy lives in utils/auth-intents.js. -->
          <div class="hero-section" v-if="!forgotPasswordMode">
            <p class="auth-eyebrow">
              <span class="auth-eyebrow-mark">✦</span>
              {{ intentCopy.eyebrow || (loginCurrentTab === 'register' ? 'Join the circle' : 'Welcome back') }}
            </p>
            <h1>{{ intentCopy.headline }}</h1>
            <p v-for="(line, i) in intentCopy.lines" :key="i" class="auth-lead">{{ line }}</p>
            <!-- A members-only link for someone who may not have a place yet:
                 a reading needs none before the draw (navigation API §6). -->
            <button v-if="intentCopy.secondary" type="button" class="auth-secondary" data-testid="auth-start-reading"
                    @click="startWithReading">{{ intentCopy.secondary }}</button>
          </div>
          <div class="modal-content">
            <div class="auth-body">
              <!-- The door after a reading made for someone (R3.9): an email's
                   code comes first there, the password second. The code is the
                   members-only one; a new person picks the password and
                   registers. Login / Register come back with the password. -->
              <!-- Continue with Google (2026-10-31): first, with a thin "or"
                   before the ways in below. Only with a client ID configured;
                   without one the form is exactly as it was. -->
              <google-sign-in v-if="showGoogle && codeFirst" :client-id="googleClientId" :error="googleError"
                              :terms-url="SITE_TERMS" :privacy-url="SITE_PRIVACY" @credential="signInWithGoogle" />
              <div class="tabs" v-if="!forgotPasswordMode && !(codeFirst && loginStep !== 'password')">
                <button :class="{ 'active-tab': currentTab === 'login' }" @click="updateCurrentTab('login')">
                  Login
                </button>
                <button :class="{ 'active-tab': currentTab === 'register' }" @click="updateCurrentTab('register')">
                  Register
                </button>
              </div>
              <google-sign-in v-if="showGoogle && !codeFirst" :client-id="googleClientId" :error="googleError"
                              :terms-url="SITE_TERMS" :privacy-url="SITE_PRIVACY" @credential="signInWithGoogle" />
              <div class="auth-reset-head" v-if="forgotPasswordMode">
                Request a Password Reset Email
              </div>

              <div v-if="currentTab === 'login' && !forgotPasswordMode">
                <!-- Identifier first (Chris, 2026-10-31): one box, then a
                     texted code for a phone or a password for an email. Every
                     email gets the same password step (nothing is asked of the
                     server), so the screen never says who has an account. -->
                <form v-if="loginStep === 'contact'" @submit.prevent="continueWith">
                  <input class="input-field" type="text" v-model="loginEmail" placeholder="Email or phone" required
                         autocomplete="username" autocapitalize="off" spellcheck="false" aria-label="Email or phone" data-testid="auth-contact" />
                  <!-- A phone: the carrier's words, and texts about arrivals
                       only if they tick the box (it starts unticked). -->
                  <template v-if="codeIsPhone">
                    <p class="auth-sms-line" data-testid="auth-sms-line">
                      We'll text you a sign-in code. Msg &amp; data rates may apply. Reply STOP to stop, HELP for help.
                      <a :href="SITE_TERMS" target="_blank" rel="noopener noreferrer">Terms</a>
                      <span aria-hidden="true"> · </span>
                      <a :href="SITE_PRIVACY" target="_blank" rel="noopener noreferrer">Privacy</a>
                    </p>
                    <label class="tos-consent auth-texts-ok" data-testid="auth-texts-ok">
                      <input type="checkbox" v-model="textsOk" /><span>Also text me when someone leaves me something</span>
                    </label>
                  </template>
                  <button type="submit" class="submit-button" data-testid="auth-continue" :disabled="state.store.is_loading">
                    <span v-if="state.store.is_loading"><span class="spinner"></span> One moment...</span>
                    <span v-else>Continue</span>
                  </button>
                  <p v-if="codeError" class="auth-code-err" role="alert">{{ codeError }}</p>
                </form>
                <template v-else>
                  <!-- Who is coming in, and the way back to step 1. -->
                  <div class="auth-who" data-testid="auth-who">
                    <span class="auth-who-name">{{ shownContact }}</span>
                    <button type="button" class="auth-link auth-who-change" data-testid="auth-change" @click="changeContact">Change</button>
                  </div>
                  <form v-if="loginStep === 'password'" @submit.prevent="login(false)">
                    <input class="input-field" type="password" v-model="loginPassword" placeholder="Password" required
                           autocomplete="current-password" aria-label="Password" />
                    <button type="submit" class="submit-button" data-testid="auth-login" :disabled="state.store.is_loading">
                      <span v-if="state.store.is_loading">
                        <span class="spinner"></span> Logging in...
                      </span>
                      <span v-else>Login</span>
                    </button>
                    <div class="auth-alt auth-alt-col">
                      <button type="button" class="auth-link" data-testid="auth-email-code" :disabled="state.store.is_loading"
                              @click="requestCode">Email me a code instead</button>
                      <button type="button" class="auth-link" @click="updateForgotPasswordMode(true)">Forgot password?</button>
                    </div>
                    <p v-if="codeError" class="auth-code-err" role="alert">{{ codeError }}</p>
                  </form>
                  <form v-else @submit.prevent="signInWithCode()">
                    <!-- A phone: the line and the choice stay in view. -->
                    <template v-if="codeIsPhone">
                      <p class="auth-sms-line" data-testid="auth-sms-line">
                        We'll text you a sign-in code. Msg &amp; data rates may apply. Reply STOP to stop, HELP for help.
                        <a :href="SITE_TERMS" target="_blank" rel="noopener noreferrer">Terms</a>
                        <span aria-hidden="true"> · </span>
                        <a :href="SITE_PRIVACY" target="_blank" rel="noopener noreferrer">Privacy</a>
                      </p>
                      <label class="tos-consent auth-texts-ok" data-testid="auth-texts-ok">
                        <input type="checkbox" v-model="textsOk" /><span>Also text me when someone leaves me something</span>
                      </label>
                    </template>
                    <p class="auth-code-said">{{ codeMessage }}</p>
                    <input class="input-field auth-code" v-model="loginCode" type="text" inputmode="numeric"
                           autocomplete="one-time-code" maxlength="6" placeholder="000000" aria-label="Six-digit code" />
                    <button type="submit" class="submit-button" data-testid="auth-code-continue" :disabled="state.store.is_loading">
                      <span v-if="state.store.is_loading"><span class="spinner"></span> Signing in...</span>
                      <span v-else>Continue</span>
                    </button>
                    <div class="auth-alt auth-alt-col">
                      <button type="button" class="auth-link" data-testid="auth-resend" :disabled="state.store.is_loading" @click="requestCode">Resend code</button>
                      <button v-if="codeFirst && !codeIsPhone" type="button" class="auth-link" data-testid="auth-use-password"
                              @click="usePassword">Use my password instead</button>
                      <button type="button" class="auth-link" data-testid="auth-different" @click="changeContact">Use a different email or phone</button>
                    </div>
                    <p v-if="codeError" class="auth-code-err" role="alert">{{ codeError }}</p>
                  </form>
                </template>
              </div>

              <div v-if="currentTab === 'login' && forgotPasswordMode">
                <form @submit.prevent="requestResetPassword">
                  <input class="input-field" type="email" v-model="resetPasswordEmail" placeholder="Email" required />
                  <button class="submit-button">Reset Password</button>
                  <div class="auth-alt">
                    <button type="button" class="auth-link" @click="updateForgotPasswordMode(false)">Back to login</button>
                  </div>
                </form>
              </div>

              <div v-if="currentTab === 'register'">
                <form @submit.prevent="register">
                  <input class="input-field" type="email" v-model="registerEmail" placeholder="Email" required />
                  <input
                    class="input-field"
                    type="password"
                    v-model="registerPassword"
                    placeholder="Password"
                    required
                  />
                  <label class="tos-consent">
                    <input type="checkbox" v-model="agreedToTerms" />
                    <span>
                      I agree to the
                      <a :href="termsUrl" target="_blank" rel="noopener">Terms of Service</a>
                      and
                      <a :href="privacyUrl" target="_blank" rel="noopener">Privacy Policy</a>.
                    </span>
                  </label>
                  <p class="auth-consent">
                    By creating a place here you agree to the
                    <a href="https://mysticaida.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
                    and
                    <a href="https://mysticaida.ai/acceptable-use" target="_blank" rel="noopener noreferrer">Acceptable Use</a>.
                  </p>
                  <button class="submit-button" :disabled="state.store.is_loading || !agreedToTerms">
                    <span v-if="state.store.is_loading">
                      <span class="spinner"></span> Registering...
                    </span>
                    <span v-else>Register</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <!-- Login/Register Modal End -->
  </div>
</template>

<script>
import CloseIcon from './CloseIcon.vue';
import GoogleSignIn from './GoogleSignIn.vue';
import {getCsrfToken} from "@/utils/general-utils";

import axios from "@/axiosConfig";
import { store, static_store } from '@/store';
import { parseAxiosError } from '@/utils/general-utils';
import { acceptTos } from '@/api-service';
import { resolveAuthIntent, codeFirst } from '@/utils/auth-intents';
import { clearAuthIntent } from '@/utils/request-auth';
import { track, setUser } from '@/utils/analytics';
import { contactKind, isPhoneish, phoneDigits, formatPhone } from '@/utils/gift-send';

// The website's pages (Mystic-Aida-website router: /terms; the privacy
// policy opens on /privacy-policy).
const SITE_TERMS = 'https://mysticaida.ai/terms';
const SITE_PRIVACY = 'https://mysticaida.ai/privacy-policy';
const SMS_UNAVAILABLE = "Texting isn't available yet. Use your email for now.";
const NOT_A_CONTACT = "That doesn't look like an email or a phone number.";
const NOT_WHOLE = 'Add the whole number, with the area code.';
// The server's reasons, said as plain sentences (its own words have no full stop).
const CODE_TROUBLE = {
  bad_code: "That code didn't match. Check it and try again.",
  expired: 'That code expired. Tap Resend code for a new one.',
  invalid_contact: NOT_A_CONTACT,
  sms_unavailable: SMS_UNAVAILABLE,
};
function codeTrouble(error) {
  const data = error?.response?.data || {};
  return CODE_TROUBLE[data.reason] || data.error || parseAxiosError(error);
}
import {reactive} from "vue";
const state = reactive({
  store,
  static_store,
  querent_subscription_expiration: '',
});

export default {
  components: {
    CloseIcon,
    GoogleSignIn,
  },
  props: {
    showLogin: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loginCurrentTab: 'login', // Default to 'login'
      forgotPasswordMode: false,
      currentTab: 'login',
      loginEmail: '',
      loginPassword: '',
      // Identifier first: 'contact' (the one box) | 'password' (an email) |
      // 'code' (a texted code, or an email's code). The code, what went wrong.
      loginStep: 'contact',
      codeMessage: '',
      loginCode: '',
      codeError: '',
      // A phone in the code box: the unticked "Also text me…" (texts_ok).
      textsOk: false,
      // Continue with Google: what went wrong, if anything.
      googleError: '',
      SITE_TERMS,
      SITE_PRIVACY,
      registerEmail: '',
      registerPassword: '',
      resetPasswordEmail: '',
      loginTabClicked: false,
      registerTabClicked: false,
      backToLoginClicked: false,
      // Compliance: new registrants must affirm 18+ and accept Terms + Privacy.
      agreedToTerms: false,
      state,
    };
  },
  watch: {
    // immediate: the form can be mounted already open (a fresh load of an
    // emailed /?signin= link), with no closed-to-open change to react to.
    showLogin: {
      immediate: true,
      handler(isOpen) {
        if (!isOpen) return;
        // Honor the tab the opener asked for: a "Join The Community" CTA sets
        // login_current_tab = 'register' before showing the modal, so it should
        // land on the Register tab rather than the default Login tab.
        const requested = state.store.login_current_tab;
        if (requested === 'login' || requested === 'register') {
          this.currentTab = requested;
          this.loginCurrentTab = requested;
        }
        this.forgotPasswordMode = false;
        this.loginStep = 'contact';
        this.codeError = '';
        // The send door (R3.9): the code is the way in, on the login side.
        if (this.codeFirst) {
          this.currentTab = 'login';
          this.loginCurrentTab = 'login';
        }
        // An email the opener already knew (the care package door recognises the
        // recipient's address). Use it once, then let go of it.
        const known = state.store.login_prefill_email;
        if (known) {
          this.loginEmail = known;
          state.store.login_prefill_email = '';
        }
        // The emailed sign-in link (?signin=<token>): sign in with it right away.
        const linkToken = state.store.login_signin_token;
        if (linkToken) {
          state.store.login_signin_token = '';
          this.signInWithCode(linkToken);
        }
      },
    },
    loginStep() { this.codeError = ''; },
  },
  computed: {
    // The hero copy for whatever the seeker was reaching for when the gate
    // fired. Falls back to the general pitch when there is no intent.
    intentCopy() { return resolveAuthIntent(state.store.authIntent); },
    // The door after a reading made for someone offers the code first (R3.9).
    codeFirst() { return codeFirst(state.store.authIntent); },
    // Links to the Django-served legal pages (same origin as the API).
    termsUrl() { return `${static_store.apiUrl}/terms/`; },
    // The code box holds a phone (phone characters, three digits or more).
    codeIsPhone() { return !String(this.loginEmail || '').includes('@') && isPhoneish(this.loginEmail); },
    // The header of steps 2: what was typed (a phone as it will be texted).
    shownContact() {
      const typed = (this.loginEmail || '').trim();
      return this.codeIsPhone && contactKind(typed) === 'phone' ? formatPhone(phoneDigits(typed)) : typed;
    },
    // One privacy policy across the app: the website's page (it carries the
    // texting section, 2026-10-31).
    privacyUrl() { return SITE_PRIVACY; },
    // Continue with Google shows only with a client ID (window.env
    // VUE_APP_GOOGLE_OAUTH_CLIENT_ID), and not on the reset screen.
    googleClientId() { return state.static_store.google_oauth_client_id || ''; },
    showGoogle() { return !!this.googleClientId && !this.forgotPasswordMode; },
  },
  methods: {
    startWithReading() {
      clearAuthIntent();
      this.$emit('update:showLogin', false);
      window.dispatchEvent(new CustomEvent('auth-start-reading'));
    },
    handleCloseClick() {
      state.store.is_loading = false;
      // Closing abandons whatever they were reaching for. Without this the next
      // deliberate "Reveal Myself" inherits the last gate's hero and its resume.
      clearAuthIntent();
      this.$emit('update:showLogin', false);
    },
    // Everything a successful sign-in does, whichever way they came in: the
    // token and ids, the session hand-over, the events, the modal closing.
    finishLogin(response, { beforeSessionId, event, method }) {
      const after_login_sesion_id = response.data.session_id;

      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('querent_id', response.data.querent_id);
      localStorage.setItem('session_id', after_login_sesion_id);

      state.store.session_id = after_login_sesion_id;
      state.store.querent_id = response.data.querent_id;
      state.store.querent_carat_balance = response.data.querent_carat_balance;
      state.store.querent_carat_balance_loaded = true;
      state.store.querent_email = response.data.querent_email;
      state.store.querent_subscription_expiration = response.data.querent_subscription_expiration;
      state.store.querent_subscribed = response.data.querent_subscribed;
      state.store.reading_sessions = response.data.reading_sessions.filter(x => x.initial_question);
      setUser(response.data.querent_id);
      track(event, { method });

      if (beforeSessionId !== after_login_sesion_id) {
        if (state.store.session_id) {
          this.$emit('resetSocketSession');
        }
      }

      this.$emit('justLoggedIn');

      this.$notify({
        text: 'Login Successful',
        type: 'success',
        duration: 10
      });

      this.$emit('loginSuccess', {
        showLogin: false,
      });
    },
    async sessionReady(timeoutMs = 10000) {
      const started = Date.now();
      while (!localStorage.getItem('session_id') && Date.now() - started < timeoutMs) {
        await new Promise((r) => setTimeout(r, 200));
      }
      return localStorage.getItem('session_id');
    },
    // What the code is asked for with: an email exactly as before ({ email }),
    // a phone as { contact } in E.164. null: a number not whole yet.
    codeContact() {
      const typed = (this.loginEmail || '').trim();
      if (!this.codeIsPhone) return { email: typed };
      return contactKind(typed) === 'phone' ? { contact: phoneDigits(typed) } : null;
    },
    // Step 1's Continue: a phone gets its code now; an email goes to the
    // password step (the send door, R3.9: its code first). Nothing about the
    // email is asked of the server here, so every email looks the same.
    continueWith() {
      this.codeError = '';
      const typed = (this.loginEmail || '').trim();
      if (this.codeIsPhone) {
        if (contactKind(typed) !== 'phone') { this.codeError = NOT_WHOLE; return; }
        return this.requestCode();
      }
      if (!this.isValidEmail(typed)) { this.codeError = NOT_A_CONTACT; return; }
      this.loginEmail = typed;
      if (this.codeFirst) return this.requestCode();
      this.loginPassword = '';
      this.loginStep = 'password';
    },
    // "Change" and "Use a different email or phone": back to the one box.
    changeContact() {
      this.loginStep = 'contact';
      this.loginCode = '';
      this.loginPassword = '';
      this.codeError = '';
    },
    usePassword() { this.loginPassword = ''; this.loginStep = 'password'; },
    // The code, sent (a phone's Continue, "Email me a code instead", Resend).
    // The answer is the same whether or not the email or phone has an account.
    async requestCode() {
      this.codeError = '';
      const who = this.codeContact();
      if (!who) { this.codeError = NOT_WHOLE; return; }
      state.store.is_loading = true;
      try {
        const response = await axios.post(`${state.static_store.apiUrl}/auth/request-code/`, who);
        // Texting not on yet: the server says so instead of sending.
        if (response?.data?.ok === false) {
          this.codeError = response.data.reason === 'sms_unavailable'
            ? SMS_UNAVAILABLE : (response.data.error || parseAxiosError(null));
          return;
        }
        this.loginStep = 'code';
        this.loginCode = '';
        // A phone: the last four, so they know which phone to look at.
        this.codeMessage = who.contact ? `We texted a code to •••${who.contact.slice(-4)}.` : (response?.data?.message || '');
      } catch (error) {
        this.codeError = codeTrouble(error);
      } finally {
        state.store.is_loading = false;
      }
    },
    // Step two: the six digits, or the emailed link's token.
    async signInWithCode(linkToken = '') {
      this.codeError = '';
      state.store.is_loading = true;
      // Opened from the emailed link on a new device, the app may still be
      // starting its session; sign-in hands that session over, so wait for it.
      const beforeSessionId = await this.sessionReady();
      const who = linkToken ? null : (this.codeContact() || {});
      const code = (this.loginCode || '').trim();
      const body = linkToken
        ? { token: linkToken, session_id: beforeSessionId }
        : who.contact
          ? { contact: who.contact, code, texts_ok: !!this.textsOk, session_id: beforeSessionId }
          : { email: (this.loginEmail || '').trim(), code, session_id: beforeSessionId };
      try {
        await getCsrfToken();
        const response = await axios.post(`${state.static_store.apiUrl}/auth/sign-in-code/`, body);
        if (response?.data?.key) {
          // A texted code to a new number made the account (`created`, as
          // Google's): a sign-up, with first-run and the referral claim.
          const created = !!response.data.created;
          if (created) state.store.just_registered = true;
          this.finishLogin(response, { beforeSessionId, event: created ? 'sign_up' : 'login',
            method: who && who.contact ? 'phone_code' : 'email_code' });
          if (created && state.store.cf) this.updateCf();
        }
      } catch (error) {
        // A spent emailed link: back to the one box, saying why.
        if (linkToken) this.loginStep = 'contact';
        this.codeError = codeTrouble(error);
      } finally {
        state.store.is_loading = false;
      }
    },
    // Continue with Google: Google's ID token goes to the server with the
    // visitor's session; the answer is the same payload as the other ways in,
    // plus `created` (a new account: a sign_up, first-run and the referral
    // claim, as email sign-up does).
    async signInWithGoogle(credential) {
      this.googleError = '';
      state.store.is_loading = true;
      const beforeSessionId = await this.sessionReady();
      try {
        await getCsrfToken();
        const response = await axios.post(`${state.static_store.apiUrl}/auth/google/`,
          { credential, session_id: beforeSessionId });
        if (response?.data?.key) {
          const created = !!response.data.created;
          if (created) state.store.just_registered = true;
          this.finishLogin(response, { beforeSessionId, event: created ? 'sign_up' : 'login', method: 'google' });
          if (created && state.store.cf) this.updateCf();
        }
      } catch (error) {
        this.googleError = error?.response?.data?.error || parseAxiosError(error);
      } finally {
        state.store.is_loading = false;
      }
    },
    updateForgotPasswordMode(mode) {
      if (mode && !this.resetPasswordEmail && this.loginEmail.includes('@')) this.resetPasswordEmail = this.loginEmail.trim();
      this.forgotPasswordMode = mode;
    },
    updateCurrentTab(tab) {
      this.loginCurrentTab = tab;
      this.currentTab = tab;
    },
async login(isAfterRegister = false) {
  await getCsrfToken();

  if (isAfterRegister) {
    state.store.just_registered = true;
  }

  try {
    state.store.is_loading = true;

    const before_login_session_id = localStorage.getItem('session_id');

    const response = await axios.post(
      `${state.static_store.apiUrl}/dj-rest-auth/login/`,
      {
        username: this.loginEmail,
        password: this.loginPassword,
        session_id: localStorage.getItem('session_id'),
        post_registration: isAfterRegister,
        reader_id: state.store.current_reader
      }
    );

    if (response?.data?.key) {
      this.finishLogin(response, { beforeSessionId: before_login_session_id,
        event: isAfterRegister ? 'sign_up' : 'login', method: 'email' });
    } else {
      // Should rarely hit this because failed logins usually throw
      this.$notify({
        text: 'Invalid Credentials',
        type: 'error',
      });
    }

  } catch (error) {
    console.log('Login response error', error);
    this.$notify({
      title: 'Login Failed',
      text: parseAxiosError(error) || 'Invalid credentials. Please try again.',
      type: 'error',
    });
  } finally {
    state.store.is_loading = false;
  }
}
,
    async register() {
      if (state.store.is_loading) {
        return;
      }
      if (!this.agreedToTerms) {
        this.$notify({ text: 'Please accept the Terms of Service and Privacy Policy.', type: 'error' });
        return;
      }
      if (!this.isValidEmail(this.registerEmail)) {
        console.log('Invalid email');
        this.$notify({
          text: 'Invalid Email',
          type: 'error',
        });
        state.store.is_loading = false;
        return;
      }
      if (!this.isValidPassword(this.registerPassword)) {
        console.log('Invalid password');
        this.$notify({
          text: 'Invalid Password',
          type: 'error',
        });
        state.store.is_loading = false;
        return;
      }
      state.store.is_loading = true;

      try {
        const registerResponse = await axios.post(
          `${state.static_store.apiUrl}/dj-rest-auth/registration/`,
          {
            email: this.registerEmail,
            username: this.registerEmail,
            password1: this.registerPassword,
            password2: this.registerPassword,
          }
        );

        if (registerResponse.status === 201 || registerResponse.status === 204) {
          this.loginEmail = this.registerEmail;
          this.loginPassword = this.registerPassword;
          await this.login(true);

          // Record ToS + Privacy acceptance against the now-logged-in seeker.
          try { await acceptTos(state.store.session_id); } catch (e) { /* non-blocking */ }

          if(state.store.cf !== ''){
            this.updateCf();
          }
        } else {
          this.$notify({
            text: 'Invalid credentials',
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Registration error:', error);
        this.$notify({
          title: 'Error',
          text: parseAxiosError(error),
          type: 'error',
        });
      } finally {
        state.store.is_loading = false;
      }
    },
    async requestResetPassword() {
      try {
        await axios.post(`${state.static_store.apiUrl}/auth/request-password-reset/`, {
          email: this.resetPasswordEmail,
        });
        this.$notify({
          text: 'Password reset email sent.',
          type: 'success',
        });
        this.updateForgotPasswordMode(false);
      } catch (error) {
        console.error('Password reset error:', error);
        this.$notify({
          title: 'Error',
          text: parseAxiosError(error),
          type: 'error',
        });
      }
    },
    updateCf() {
      try {
        axios.get(`${state.static_store.apiUrl}/quest/register-cf/`, {
          params: {
            cf_id: state.store.cf,
            querent_id: state.store.querent_id,
            // The referral credit is paid only to a signed-in member's claim.
            session_id: state.store.session_id,
          },
        });
      } catch (error) {
        console.error('Error updating cf.', error);
      }
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    isValidPassword(password) {
      // Add your password validation logic here
      return password.length >= 6;
    },
  },
};
</script>


<style scoped>
input, textarea, select, button {
  font-size: 16px;
}

/* The single gold action. This was declared twice — `padding: 20px 5px` here and
   `padding: 4px` further down, which won — so the button rendered as a cramped
   shrink-to-fit lozenge. One rule now, full width, so the primary action is
   unmistakably the primary action. */
.submit-button {
  display: block;
  width: 100%;
  background: linear-gradient(180deg, #FBE7B4 0%, #E3C471 52%, #C9A94E 100%);
  color: #1a0f2e;
  border: none;
  padding: 13px 18px;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Cinzel', serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin: 6px 0 0;
  transition: filter 0.18s ease, transform 0.12s ease;
}

.submit-button:hover:not(:disabled) { filter: brightness(1.07); }
.submit-button:active:not(:disabled) { transform: translateY(1px); }
.submit-button:focus-visible {
  outline: 2px solid #FFDFA3;
  outline-offset: 2px;
}
.submit-button:disabled { opacity: 0.42; cursor: default; }

.tos-consent {
  display: flex; align-items: flex-start; gap: 9px;
  margin: 12px 2px 4px; font-size: 12.5px; line-height: 1.45;
  color: rgba(232, 216, 255, 0.72); text-align: left;
}
.tos-consent {
  font-size: 13.5px;
}
.tos-consent input {
  margin-top: 2px; flex-shrink: 0;
  accent-color: #C9A94E;   /* checkbox reads as gold, not OS blue */
  /* A 15px box is a hard tap on a phone; 20px is the smallest that is fair. */
  width: 20px; height: 20px;
}

/* What registering actually agrees to, in one plain sentence. The links carry
   their own padding so a thumb can land on them. */
.auth-consent {
  margin: 8px 2px 4px; font-size: 13px; line-height: 1.6; text-align: left;
  color: rgba(232, 216, 255, 0.62);
}
.auth-consent a {
  /* The padding is the tap target; the negative margin keeps the sentence
     spaced as if it were not there. */
  display: inline-block; padding: 8px 4px; margin: -8px -4px; color: #d8b87a;
  text-decoration: underline; text-underline-offset: 2px;
}
.auth-consent a:hover { color: #fff7e6; }
.tos-consent a { color: #d8b87a; text-decoration: underline; text-underline-offset: 2px; }
.tos-consent a:hover { color: #fff7e6; }

/* Secondary route out (forgot password / back). A real <button>, so it's
   keyboard reachable — it used to be a bare <span> with a click handler. */
.auth-alt { margin-top: 14px; }
/* The carrier's words under a phone, and the unticked "Also text me…". */
.auth-sms-line { margin: -4px 2px 10px; font-size: 12.5px; line-height: 1.55; text-align: left; color: rgba(232, 216, 255, 0.7); }
.auth-sms-line a { display: inline-block; padding: 6px 2px; margin: -6px 0; color: #d8b87a; text-decoration: underline; text-underline-offset: 2px; }
.auth-sms-line a:hover { color: #fff7e6; }
.auth-texts-ok { margin: 0 2px 14px; }
/* Steps 2: who is coming in, and "Change". */
.auth-who { display: flex; align-items: baseline; justify-content: center; gap: 10px; margin: 0 2px 14px; flex-wrap: wrap; }
.auth-who-name { font-size: 15px; color: #f3e6ff; overflow-wrap: anywhere; }
.auth-who-change { font-size: 13px; padding: 6px 4px; }
.auth-alt-col { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.auth-code-said { font-size: 13px; line-height: 1.5; color: rgba(236, 228, 255, 0.85); margin: 4px 0 12px; }
.auth-code { text-align: center; letter-spacing: 8px; font-size: 22px; }
.auth-code-err { margin: 10px 0 0; font-size: 13px; color: #ffb4a8; }
.auth-link {
  background: none; border: none; padding: 12px 8px; cursor: pointer;
  font-family: inherit; font-size: 12.5px; color: rgba(195, 169, 255, 0.7);
  text-decoration: underline; text-underline-offset: 3px;
  text-decoration-color: rgba(195, 169, 255, 0.3);
}
.auth-link:hover { color: #FFDFA3; text-decoration-color: rgba(255, 223, 163, 0.6); }
.auth-link:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; border-radius: 4px; }

/* The threshold. A single gold hairline divides the invitation from the form —
   the one structural device in the modal, and it marks a real transition
   (read about it / act on it) rather than decorating the gap. */
.modal-content {
  background: none;
  border: none;
  border-top: 1px solid rgba(240, 217, 163, 0.16);
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  width: 100%;
  padding: 0;
}

.auth-body { padding: 20px 26px 26px; }

.auth-reset-head {
  font-family: 'Cinzel', serif;
  font-size: 15px;
  color: #FFDFA3;
  padding: 6px 0 18px;
}

/* The invitation. Sits directly on the vault surface — the flat purple block it
   used to be fought the modal it lived in. Cinzel is the app's ritual voice. */
.hero-section {
  text-align: left;
  padding: 4px 26px 20px;
  background: none;
  color: #E8D8FF;
  border-radius: 0;
  margin-bottom: 0;
}

.hero-section h1 {
  font-family: 'Cinzel', serif;
  font-size: 25px;
  line-height: 1.2;
  font-weight: 600;
  color: #FFDFA3;
  margin: 0 0 12px;
}

.auth-eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Cinzel', serif;
  font-size: 10.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(232, 216, 255, 0.55);
  margin: 0 0 8px;
}

.auth-eyebrow-mark { color: #C9A94E; }

.auth-secondary {
  display: block; width: 100%; min-height: 44px; margin: 10px 0 4px; padding: 10px 16px; cursor: pointer;
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px; color: #f0d9a3;
  background: transparent; border: 1px solid rgba(201, 169, 78, 0.6); border-radius: 999px;
}
.auth-secondary:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 3px; }

.auth-lead {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 16px;
  line-height: 1.45;
  color: rgba(232, 216, 255, 0.85);
  margin: 0 0 6px;
}

.hero-content h1,
.hero-content ul,
.hero-content p {
  margin: 0 0 10px 0;
  padding: 0;


}

.hero-content ul {
  list-style-type: disc; /* Optional: change to disc if you prefer bullet points */
  padding-left: 20px; /* Indentation for list items */
}

.hero-content li {
  margin-bottom: 5px; /* Space between list items */
}

.hero-content p {
  margin-top: 10px; /* Space above the paragraph */
}

.hero-content a {
  color: #ffffff; /* Link color */
  cursor: pointer;
  text-decoration: underline; /* Optional: underline the link */
}

.hero-content a:hover {
  color: #cccccc; /* Optional: change color on hover */
}

.big-button {
  background-color: #663399;
  color: white;
  border: none;
  padding: 15px 15px;
  border-radius: 7.5px;
  cursor: pointer;
  font-size: 1.5em;
}

.login-modal-overlay {
  position: fixed;
  inset: 0;
  /* Keep the sheet clear of the iOS home bar. */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
  background-color: rgba(6, 0, 14, 0.78);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  /* Above every in-app modal (SeekerModal 9000, image lightbox 9100) so an auth
     prompt fired from inside a modal lands on top, never behind it. */
  z-index: 10000;
}

/* One continuous vault surface. This was a white card with a second white card
   nested inside it, which read as a stock form dropped into a dark, candlelit
   product. Same tokens as the community modals: #14071f, gold hairline, 18px. */
.login-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0 16px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: #14071f;
  border: 1px solid rgba(240, 217, 163, 0.22);
  border-radius: 18px;
  padding: 0;
  color: #E8D8FF;
  box-shadow: 0 26px 60px -16px rgba(0, 0, 0, 0.85);
  /* The signature: one candlelight bloom behind the invitation. Everything else
     stays quiet so this is the only flourish in the modal. */
  background-image:
    radial-gradient(120% 80% at 50% -10%, rgba(102, 51, 153, 0.55) 0%, rgba(102, 51, 153, 0.12) 45%, transparent 70%),
    radial-gradient(60% 40% at 50% 0%, rgba(255, 223, 163, 0.14) 0%, transparent 60%);
  background-repeat: no-repeat;
}

/* The modal now owns padding: 0 so the hairline can span edge to edge; the
   header supplies its own inset. The border-bottom is dropped locally — it
   comes from the shared styles/modal.css rule (used by 3 other modals, so the
   global stays put) and drew a second divider competing with the threshold. */
.x-modal-header {
  display: flex;
  justify-content: flex-end;
  padding: 14px 16px 0;
  border-bottom: none;
}

.x-modal-close-button {
  border: none;
  background: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: grey;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
/* Gold ✦ markers instead of browser bullets — the same mark the eyebrow uses,
   so the list reads as part of the invitation rather than a spec sheet. The old
   :first-child rule faked a heading out of a list item; that's a real <p> now. */
.hero-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.hero-section ul li {
  position: relative;
  padding-left: 18px;
  margin-bottom: 5px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(232, 216, 255, 0.9);
}

.hero-section ul li::before {
  content: '✦';
  position: absolute;
  left: 0;
  top: 0;
  font-size: 9px;
  line-height: 1.9;
  color: rgba(201, 169, 78, 0.85);
}
.close-button{
  margin-top: 0;
  margin-bottom: 5px;
}
/* Eventually needs to be refactored, for login form styles: */



/* Same gold-underline tab idiom as the community tab bar, instead of a grey box. */
.tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 18px;
}

.tabs button {
  flex: 0 0 auto;
  background: none;
  outline: none;
  cursor: pointer;
  padding: 8px 18px 10px;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  font-family: 'Cinzel', serif;
  font-size: 14px;
  color: rgba(195, 169, 255, 0.55);
  transition: color 0.2s ease, border-color 0.2s ease;
}

.tabs button:hover { color: rgba(195, 169, 255, 0.85); background: none; }

.active-tab {
  color: #FFDFA3 !important;
  border-bottom-color: #FFDFA3 !important;
  background: none !important;
}

.input-field {
  width: 100%;
  /* Explicit — inputs don't inherit the global border-box here, so width:100%
     plus padding pushed the field ~28px past the modal's content column.
     .cv-modal-input sets this for the same reason. */
  box-sizing: border-box;
  display: block;
  margin: 0 0 12px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 10px;
  color: #E8D8FF;
  /* 16px, because nothing else stops iOS Safari zooming the page on focus:
     the viewport meta has no maximum-scale, and it should not get one. */
  font-size: 16px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.input-field::placeholder { color: rgba(232, 216, 255, 0.4); }

.input-field:focus {
  outline: none;
  border-color: rgba(240, 217, 163, 0.55);
  box-shadow: 0 0 0 3px rgba(240, 217, 163, 0.12);
}
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  /* Reads against the gold button, not the old purple one. */
  border: 2px solid rgba(26, 15, 46, 0.9);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner { animation-duration: 2.4s; }
  .submit-button, .input-field, .tabs button { transition: none; }
  .fade-enter-active, .fade-leave-active { transition: none; }
}
</style>
